/**
 * Decodes a stage's `queryConfig` (sort config strings, group config, column
 * alias key) into the snake_case parameter bag used by the remittance-item
 * query when the user is on a stage-number tab.
 *
 * Caller is responsible for picking the current stage from the stages lookup
 * and counting how many stage tabs exist in total (drives `page_size`).
 */

export interface IStageQueryConfig {
  sortConfig: string[];
  groupConfig: {
    limit: number;
    groupBy: string;
  };
  displayOrder: number;
  columnAliasKey: string;
}

export interface IStageQueryConfigCarrier {
  id: number;
  queryConfig: IStageQueryConfig;
}

export interface IBuildStageTabFetchParamsInput {
  currentStage: IStageQueryConfigCarrier | undefined;
  totalStageTabCount: number;
  defaultAnalystId?: number;
}

export interface IStageTabFetchParams {
  page_number: number;
  page_size: number;
  sort_column?: string;
  sort_order?: string;
  status_ids?: number[];
  group_by?: string;
  group_by_limit?: number;
  column_alias_key?: string;
  analyst_ids?: number[];
}

export class BuildStageTabFetchParamsUseCase {
  execute(input: IBuildStageTabFetchParamsInput): IStageTabFetchParams {
    const { currentStage, totalStageTabCount, defaultAnalystId } = input;

    const sortEntries: Array<[string, string]> =
      currentStage?.queryConfig.sortConfig?.map(entry => {
        const [column, order] = entry.split(" ");
        return [column, order];
      }) ?? [];
    const sortConfigMap = new Map<string, string>(sortEntries);

    const groupConfig = currentStage?.queryConfig.groupConfig;
    const groupLimit = groupConfig?.limit ?? 0;
    const defaultPageNumber = 1;

    return {
      page_number: defaultPageNumber,
      page_size: groupLimit * totalStageTabCount,
      sort_column: Array.from(sortConfigMap.keys()).join(","),
      sort_order: Array.from(sortConfigMap.values()).join(","),
      status_ids: currentStage?.id ? [currentStage.id] : undefined,
      group_by: groupConfig?.groupBy ?? undefined,
      group_by_limit: groupLimit,
      column_alias_key: currentStage?.queryConfig.columnAliasKey ?? undefined,
      analyst_ids: defaultAnalystId === undefined ? undefined : [defaultAnalystId],
    };
  }
}
